import axios from "@/plugins/axios";
import utils from "@/stores/utils";

/**
 * Communications received by one customer, across every campaign.
 *
 * Powers the "Communications" tab of the customer detail, which answers
 * "what did we already tell them?" rather than "which campaign was that?".
 */

const DEFAULT_FILTERS = { offset: 0, limit: 20 };

const createState = (options = {}) => ({
    communications: [],
    communications_url: null,
    communications_count: 0,
    communications_filters: { ...DEFAULT_FILTERS },
    communications_loaded: false,
    communications_loading: false,
    communications_loading_errors: null,
    optouts: [],
    optouts_loading: false,
    _storageOptions: options || {},
});

const mutations = {
    updateCommunications(state, communications) {
        state.communications = communications;
        state.communications_loaded = true;
        state.communications_loading_errors = null;
    },
    updateCommunicationsCount(state, count) {
        state.communications_count = count;
    },
    updateCommunicationsUrl(state, url) {
        state.communications_url = url;
    },
    updateCommunicationsLoading(state, loading) {
        state.communications_loading = loading;
    },
    updateCommunicationsLoadingErrors(state, errors) {
        state.communications_loading_errors = errors;
        state.communications_loading = false;
    },
    updateCommunicationsFilters(state, filters) {
        state.communications_filters = filters;
    },
    updateOptOuts(state, optouts) {
        state.optouts = optouts;
    },
    updateOptOutsLoading(state, loading) {
        state.optouts_loading = loading;
    },
};

const actions = {
    async fetchCommunications({ commit, state }) {
        if (!state.communications_url) {
            return;
        }
        commit("updateCommunicationsLoading", true);
        try {
            const response = await axios.get(state.communications_url, {
                params: state.communications_filters,
            });
            commit("updateCommunications", response.data.results);
            commit("updateCommunicationsCount", response.data.count);
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateCommunicationsLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateCommunicationsLoading", false);
        }
    },

    async fetchOptOuts({ commit, state }) {
        if (!state.communications_url) {
            return;
        }
        commit("updateOptOutsLoading", true);
        try {
            const response = await axios.get(`${state.communications_url}optouts/`);
            commit("updateOptOuts", response.data);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateOptOutsLoading", false);
        }
    },

    /** Re-subscribe an address, always after an explicit customer request. */
    async revokeOptOut({ dispatch, state }, params) {
        try {
            const response = await axios.delete(`${state.communications_url}optouts/`, {
                params: { email: params.email },
            });
            await dispatch("fetchOptOuts");
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async sendEmail({ dispatch }, params) {
        try {
            const response = await axios.post("/api/communications/email/", params);
            await dispatch("fetchCommunications");
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async init({ commit }, params) {
        commit("updateCommunicationsUrl", params?.url || null);
        commit("updateCommunicationsFilters", {
            ...DEFAULT_FILTERS,
            ...(params?.filters || {}),
        });
        commit("updateCommunications", []);
        commit("updateOptOuts", []);
    },
};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions,
});
